import { Form, Input, Button } from 'components/common';
import { useState, useEffect, useCallback } from 'react';

import PasswordError, {
  SERVER_ERROR_MESSAGES,
} from '../../Settings/Account/PasswordError';

import { passwordCheck } from 'services/api';
import { usePost } from 'hooks';

const PasswordChange = ({
  serverError,
  passwordSecurityError,
  onChange,
  newPass,
  setNewPass,
  handleSubmit,
  loading,
  setPasswordSecurityError,
  hideChangePasswordButton,
  confirmPass,
  setConfirmPass,
}) => {
  // UI state: when to show the password rules box
  const [showRules, setShowRules] = useState(false);

  // Track last password we checked with the server to avoid duplicate calls
  const [lastCheckedPassword, setLastCheckedPassword] = useState('');

  // Client-side validation state from PasswordError
  const [validationState, setValidationState] = useState({
    hasErrors: false,
    isTooShort: false,
    isTooWeak: false,
  });

  const [
    { error: passCheckError, res: passwordCheckRes },
    postPasswordCheck,
  ] = usePost({ url: passwordCheck });

  // Notify parent of error state changes
  useEffect(() => {
    onChange(validationState.hasErrors || !!passCheckError);
  }, [validationState.hasErrors, passCheckError, onChange]);

  // Server-side password check on blur (not on every keystroke)
  const handlePasswordBlur = useCallback(() => {
    const shouldCheckPassword =
      !validationState.isTooShort &&
      !validationState.isTooWeak &&
      newPass &&
      newPass !== lastCheckedPassword;

    if (shouldCheckPassword) {
      setLastCheckedPassword(newPass);
      postPasswordCheck({ password: newPass });
    }
  }, [
    validationState.isTooShort,
    validationState.isTooWeak,
    newPass,
    lastCheckedPassword,
    postPasswordCheck,
  ]);

  // Update passwordSecurityError based on server password check result
  useEffect(() => {
    if (passwordCheckRes) {
      setPasswordSecurityError(null);
    }
    if (passCheckError) {
      setPasswordSecurityError(
        passCheckError.message === 'PASSWORD_NOT_SECURE'
          ? SERVER_ERROR_MESSAGES[passCheckError.message]
          : 'We were unable to verify this password. Please try again.'
      );
    }
  }, [setPasswordSecurityError, passwordCheckRes, passCheckError]);

  const handleValidationChange = useCallback(state => {
    setValidationState(state);
  }, []);

  const disableButton =
    !newPass ||
    !confirmPass ||
    loading ||
    validationState.hasErrors ||
    passCheckError;

  return (
    <Form canSubmit={!disableButton} onSubmit={handleSubmit}>
      <p>
        Create a strong password to secure your account. Use a mix of letters,
        numbers, and symbols for added protection.
      </p>
      <div>
        <div className='label'>New Password</div>
        <Input
          password
          placeholder='New Password'
          value={newPass}
          onChange={setNewPass}
          onFocus={() => setShowRules(true)}
          onBlur={handlePasswordBlur}
        />
      </div>
      <div>
        <div className='label'>Confirm New Password</div>
        <Input
          password
          placeholder='Confirm New Password'
          value={confirmPass}
          onChange={setConfirmPass}
          onFocus={() => setShowRules(true)}
        />
      </div>
      <PasswordError
        newPass={newPass}
        confirmPass={confirmPass}
        shouldRender={showRules}
        serverError={serverError}
        passwordSecurityError={passwordSecurityError}
        onChange={handleValidationChange}
      />
      {!hideChangePasswordButton && (
        <Button
          type='primary'
          disabled={disableButton}
          onClick={handleSubmit}
          style={{ width: '100%' }}
        >
          Set a Password
        </Button>
      )}
    </Form>
  );
};

export default PasswordChange;
